import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { array, mixed, string } from "yup";
import { countries } from "../../common/models";
import { getFields, validationFields } from "../../common/models/fields";
import { MerchantProductTypes, Roles } from "./internal";

export namespace AuthDTO {
  export namespace Responses {
    export interface UserCreated {
      userReferenceId: string;
    }
    export namespace Login {
      interface RawAccessToken {
        jti: string;
        username: string;
        credentialsId: string;
        submerchantIds?: string[];
        companyId: string;
        roles: Array<typeof Roles.T>;
        originalUser?: {
          id: string;
          username: string;
          token: string;
          type: "DELEGATE" | "IMPERSONATION";
        };
      }

      export type WithTwoFA = {
        accessToken: RawAccessToken & { authenticated: false };
        twoFactorAuth: {
          provider: string;
          extraDetails?: {
            secret: string;
            image: string;
          };
        };
        raw: string;
      };

      export type Default = {
        accessToken: RawAccessToken & {
          authenticated: true;
        };
        raw: string;
      };

      export type T = Default | WithTwoFA;

      export const defaults: Partial<T> = {};

      export function isWithTwoFA(x: T): x is WithTwoFA {
        //TODO: should be inferred automatically
        return !x.accessToken.authenticated;
      }
    }
  }

  export namespace Requests {
    export namespace UpdateMarketingSettings {
      export interface T {
        receiveMarketingMaterial: boolean;
      }
    }
    export namespace UnauthedResetPassword {
      export interface T {
        password: string;
        confirmPassword: string;
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");
        const password = {
          name: useMemo(() => t("new-password"), [t]),
          schema: validationFields.password.useSchema(),
        };
        const confirmPassword = {
          name: validationFields.confirmPassword.useName(),
          schema: validationFields.confirmPassword.useSchema(),
        };

        return useMemo(
          () =>
            getFields({
              password,
              confirmPassword,
            }),
          [
            password.name,
            password.schema,
            confirmPassword.name,
            confirmPassword.schema,
          ]
        );
      };

      export const defaults: T = {
        password: "",
        confirmPassword: "",
      };
    }

    export namespace ResetPassword {
      export interface T {
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");

        const currentPassword = {
          name: useMemo(() => t("current-password"), [t]),
          schema: useMemo(
            () =>
              string()
                .label(t("password"))
                .required(
                  t("required-field", {
                    ns: "errors",
                    field: t("password"),
                  })
                ),
            [t]
          ),
        };

        const confirmPassword = {
          name: validationFields.confirmPassword.useName(),
          schema: validationFields.confirmPassword.useSchema(),
        };

        const newPassword = {
          name: validationFields.confirmPassword.useName(),
          schema: validationFields.confirmPassword.useSchema(),
        };

        return useMemo(
          () =>
            getFields({
              newPassword,
              currentPassword,
              confirmPassword,
            }),
          [
            newPassword.name,
            newPassword.schema,
            currentPassword.name,
            currentPassword.schema,
            confirmPassword.name,
            confirmPassword.schema,
          ]
        );
      };

      export const defaults: T = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
    }
    export namespace ForgotPassword {
      export interface T {
        emailAddress: string;
      }

      export const useFields = () => {
        const emailName = validationFields.email.useName();
        const emailSchema = validationFields.email.useSchema();

        return useMemo(
          () =>
            getFields({
              emailAddress: {
                name: emailName,
                schema: emailSchema,
              },
            }),
          [emailName, emailSchema]
        );
      };

      export const defaults: T = {
        emailAddress: "",
      };
    }

    export namespace AuthRequest {
      export interface T {
        username: string;
        password: string;
      }
      export const useFields = () => {
        const { t } = useTranslation("fields");
        const username = {
          name: validationFields.email.useName(),
          schema: validationFields.email.useSchema(),
        };
        const password = {
          name: validationFields.password.useName(),
          schema: useMemo(
            () =>
              string()
                .label(t("password"))
                .required(
                  t("required-field", {
                    ns: "errors",
                    field: t("password"),
                  })
                ),
            [t]
          ),
        };

        return useMemo(
          () =>
            getFields({
              username,
              password,
            }),
          [username.name, username.schema, password.name, password.schema]
        );
      };
      export const defaults: T = {
        username: "",
        password: "",
      };
    }
    export namespace TwoFactorRequest {
      export interface T {
        provider: string;
        password: string;
      }
      export const useFields = () => {
        const provider = {
          name: validationFields.provider.useName(),
          schema: validationFields.provider.useSchema(),
        };
        const password = {
          name: validationFields.otp.useName(),
          schema: validationFields.otp.useSchema(),
        };

        return useMemo(
          () =>
            getFields({
              provider,
              password,
            }),
          [provider.name, provider.schema, password.name, password.schema]
        );
      };
      export const defaults: Partial<T> = {};
    }
    export namespace CreateIndividualUser {
      export interface T {
        emailAddress: string;
        password: string;
        confirmPassword?: string;
        firstName: string;
        middleName?: string;
        lastName: string;
        dateOfBirth: Date;
        nationality: typeof countries.T;
        token: string;
      }

      export const useFields = () => {
        const emailAddress = {
          name: validationFields.email.useName(),
          schema: validationFields.email.useSchema(),
        };
        const password = {
          name: validationFields.password.useName(),
          schema: validationFields.password.useSchema(),
        };
        const confirmPassword = {
          name: validationFields.confirmPassword.useName(),
          schema: validationFields.confirmPassword.useSchema(),
        };
        const firstName = {
          name: validationFields.firstName.useName(),
          schema: validationFields.firstName.useSchema(),
        };
        const middleName = {
          name: validationFields.middleName.useName(),
          schema: validationFields.middleName.useSchema(),
        };
        const lastName = {
          name: validationFields.lastName.useName(),
          schema: validationFields.lastName.useSchema(),
        };
        const dateOfBirth = {
          name: validationFields.dateOfBirth.useName(),
          schema: validationFields.dateOfBirth.useSchema(),
        };
        const nationality = {
          name: validationFields.nationality.useName(),
          schema: validationFields.nationality.useSchema(),
        };

        return useMemo(
          () =>
            getFields({
              emailAddress,
              password,
              confirmPassword,
              firstName,
              middleName,
              lastName,
              dateOfBirth,
              nationality,
            }),
          [
            emailAddress.name,
            emailAddress.schema,
            password.name,
            password.schema,
            confirmPassword.name,
            confirmPassword.schema,
            firstName.name,
            firstName.schema,
            middleName.name,
            middleName.schema,
            lastName.name,
            lastName.schema,
            dateOfBirth.name,
            dateOfBirth.schema,
            nationality.name,
            nationality.schema,
          ]
        );
      };

      export const defaults: Partial<T> = {
        emailAddress: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        middleName: "",
        lastName: "",
      };
    }

    export namespace CreateMerchantUser {
      export interface T {
        emailAddress: string;
        firstName: string;
        companyName: string;
        lastName: string;
        dateOfBirth: Date;
        country: typeof countries.T;
        token: string;
        products: typeof MerchantProductTypes.T[];
      }

      export const useFields = () => {
        const emailAddress = {
          name: validationFields.email.useName(),
          schema: validationFields.email.useSchema(),
        };

        const firstName = {
          name: validationFields.firstName.useName(),
          schema: validationFields.firstName.useSchema(),
        };
        const companyName = {
          name: "Company Name",
          schema: useMemo(() => string().required().label("Company Name"), []),
        };
        const lastName = {
          name: validationFields.lastName.useName(),
          schema: validationFields.lastName.useSchema(),
        };
        const dateOfBirth = {
          name: validationFields.dateOfBirth.useName(),
          schema: validationFields.dateOfBirth.useSchema(),
        };
        const country = {
          name: validationFields.country.useName(),
          schema: validationFields.nationality.useSchema(),
        };

        const products = {
          name: "Products",
          schema: useMemo(
            () =>
              array()
                .of(mixed().oneOf(MerchantProductTypes.keys))
                .label("Products"),
            [MerchantProductTypes]
          ),
        };

        return useMemo(
          () =>
            getFields({
              emailAddress,
              firstName,
              companyName,
              lastName,
              dateOfBirth,
              country,
              products,
            }),
          [
            emailAddress.name,
            emailAddress.schema,
            products.name,
            products.schema,
            firstName.name,
            firstName.schema,
            companyName.name,
            companyName.schema,
            lastName.name,
            lastName.schema,
            dateOfBirth.name,
            dateOfBirth.schema,
            country.name,
            country.schema,
          ]
        );
      };

      export const defaults: Partial<T> = {
        products: [],
      };
    }

    export namespace Impersonate {
      export interface T {
        username: string;
      }

      export const useFields = () => {
        const name = validationFields.email.useName();
        const schema = validationFields.email.useSchema();
        return useMemo(
          () =>
            getFields({
              username: {
                name,
                schema,
              },
            }),
          [name, schema]
        );
      };
    }
  }
}
